/**
 * Anexo al ESF: el mismo estado abierto una cuenta más abajo.
 *
 * No es otro documento. Sale del mismo mapping que el estado al que acompaña,
 * así que no puede discrepar de él, y cada rubro enlaza con su composición. La
 * cadena completa es: estado → rubro → cuenta → apunte.
 *
 * UNA tabla continua, no una tarjeta por rubro. El anexo real del ESF tiene 23
 * rubros y 617 cuentas: en tarjetas eran 23 cabeceras idénticas repetidas y la
 * jerarquía —qué cuenta pertenece a qué rubro— se leía por la posición dentro
 * de una caja, no por la estructura de la tabla. Un estado contable se presenta
 * como una tabla jerárquica; las tarjetas son para piezas independientes, y los
 * rubros de un mismo estado no lo son.
 */

import { Link } from "react-router-dom";
import type { AnnexLine } from "../../api/types";
import { formatMoney, moneyClass } from "../../components/format";
import { useClosing } from "./closingContext";
import { StatementHead } from "./shared";

export function StatementAnnex() {
  const { package: pkg } = useClosing();
  // Qué se presenta y en qué nivel va cada fila lo decidió el backend, para las
  // tres salidas. Aquí se sumaba cada rubro EN EL NAVEGADOR, con un total que
  // ningún otro formato producía: el PDF imprimía las cuentas sin cifra de
  // rubro y el Excel las escribía sin ninguna. Un anexo contable con tres
  // totales distintos por rubro no es un anexo, son tres.
  const filas = pkg.annex.filter((row) => row.presented);
  // Cuántas columnas de saldo lleva el anexo: cuenta, rubro y sección, como en
  // la hoja `ANEXO ESF` del archivo del contador (C11 · E10 · G8).
  const pasos = Math.max(1, ...filas.map((fila) => fila.amount_step + 1));

  return (
    <>
      <StatementHead
        title="Anexo al Estado de Situación Financiera"
        homologation={pkg.homologation.esf_annex}
      />

      {filas.length === 0 && (
        <div className="card">
          <p className="muted">Sin cuentas con saldo en el período seleccionado.</p>
        </div>
      )}

      {filas.length > 0 && (
        <div className="table-wrap documento anexo-hoja">
          <table className="anexo">
            <thead>
              <tr>
                <th scope="col">Cuenta</th>
                <th scope="col">Concepto</th>
                {Array.from({ length: pasos }, (_, paso) => (
                  /* El rótulo VISIBLE va solo sobre la última, como en la hoja
                     del contador. Pero una columna sin nombre accesible es una
                     columna que un lector de pantalla anuncia vacía, y aquí hay
                     dos: quien navegue así oiría tres cifras sin saber a qué
                     nivel pertenece cada una, que es justo lo que el escalonado
                     comunica visualmente. `aria-label` lo dice sin añadir tinta. */
                  <th scope="col" className="num" key={paso} aria-label={nivelDeSaldo(paso, pasos)}>
                    {paso === pasos - 1 ? "Saldo" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila) => {
                const esCuenta = fila.amount_level === "detail";
                const esRotulo = fila.amount_level === "heading";
                return (
                  <tr
                    key={`${fila.line_id}-${fila.account_code}`}
                    className={claseFila(fila, pasos)}
                    id={esCuenta ? undefined : fila.line_id}
                  >
                    {/* «Cuenta» lleva CÓDIGOS CONTABLES, y solo las cuentas
                        tienen uno. Aquí salía el identificador del mapping
                        —`ESF_CRIPTO_INVENTARIO`— en la columna que el contador
                        lee como plan de cuentas: un dato técnico interno con
                        aspecto de código de cuenta. El enlace a la composición
                        se queda, pero sobre el rótulo, que es lo que nombra. */}
                    <td className="mono cuenta">{esCuenta ? fila.account_code : ""}</td>
                    <td className={esCuenta ? "cuenta" : undefined}>
                      {esCuenta ? (
                        fila.account_name
                      ) : (
                        <Link to={`/audit?line=${fila.line_id}`} title="Ver composición">
                          {fila.line_label}
                        </Link>
                      )}
                    </td>
                    {Array.from({ length: pasos }, (_, paso) => (
                      <td
                        className={
                          !esRotulo && fila.amount_step === paso
                            ? moneyClass(fila.closing_balance)
                            : "num"
                        }
                        key={paso}
                      >
                        {/* Un rótulo de masa TITULA; no lleva importe. Sin esta
                            guarda imprimía «0.00», que en una columna de cifras
                            se lee como un saldo en cero y no como un título. */}
                        {!esRotulo && fila.amount_step === paso
                          ? formatMoney(fila.closing_balance)
                          : ""}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Cómo se llama la columna de saldo de cada escalón.
 *
 * El anexo del contador escalona el importe: la cifra de una cuenta va en una
 * columna, la de su rubro en la siguiente y la de la sección en la última
 * (`C11 · E10 · G8` en la hoja «ANEXO ESF»). La posición ES el nivel, y en
 * pantalla eso se ve; leído en voz alta, no.
 */
function nivelDeSaldo(paso: number, pasos: number): string {
  const nombres = ["Saldo de la cuenta", "Saldo del rubro", "Saldo de la sección"];
  // El último escalón es siempre el más agregado, tenga el anexo los escalones
  // que tenga: se nombra desde el final para que un anexo con otra profundidad
  // no desplace los rótulos.
  const desdeElFinal = pasos - 1 - paso;
  return nombres[nombres.length - 1 - desdeElFinal] ?? `Saldo, nivel ${paso + 1}`;
}


/**
 * El peso de la fila sigue al ESCALÓN, no a «es o no es una cuenta».
 *
 * Había una sola clase —`rubro`— para los tres niveles de agregación, así que
 * la masa, la sección y el rubro salían con la misma franja gris y la misma
 * negrita. La columna del importe ya escalona; la franja lo desmentía
 * aplanándolo todo a un nivel, y el ojo cree antes a la franja que a la
 * posición de la cifra.
 *
 * Ahora hay tres pesos y coinciden con lo que el documento dice: el rótulo de
 * masa es un TÍTULO —filete arriba, sin franja, sin importe—, la agregación
 * más alta lleva la franja de sección, y la de debajo una tenue. La cuenta no
 * lleva ninguna.
 */
function claseFila(fila: AnnexLine, pasos: number): string | undefined {
  if (fila.amount_level === "detail") return undefined;
  if (fila.amount_level === "heading") return "rotulo-masa";
  return fila.amount_step >= pasos - 1 ? "rubro nivel-alto" : "rubro";
}
